// Org admin: configure customer-provided AI provider keys (OpenAI, Anthropic, etc.)
import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/Button';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { useAppStore } from '@/store/app';
import apiClient from '@/api';

// Server uses snake_case for primaryProvider and camelCase for the providers map.
// Keep both shapes straight via these constants.
const PROVIDER_SLUG_BY_KEY: Record<string, string> = {
  openai: 'openai',
  anthropic: 'anthropic',
  azureOpenai: 'azure_openai',
  googleGemini: 'google_gemini',
  xaiGrok: 'xai_grok',
  cohere: 'cohere',
  mistral: 'mistral'
};

const PROVIDER_KEY_BY_SLUG: Record<string, string> = Object.fromEntries(
  Object.entries(PROVIDER_SLUG_BY_KEY).map(([k, v]) => [v, k])
);

interface ProviderInfo {
  id: string;
  name: string;
  description: string;
  models: string[];
  defaultModel: string;
  setupUrl: string;
  requiresEndpoint?: boolean;
}

interface ProviderConfig {
  enabled: boolean;
  apiKey?: string | null;
  model?: string | null;
  endpoint?: string;
  deploymentName?: string;
  organization?: string;
  maxTokens?: number;
  temperature?: number;
}

interface AIConfig {
  enabled: boolean;
  primaryProvider: string | null;
  providers: Record<string, ProviderConfig>;
  features: Record<string, boolean>;
  usage?: {
    totalTokensThisMonth?: number;
    totalCostThisMonth?: number;
    monthlyTokenLimit?: number | null;
    monthlyCostLimit?: number | null;
    lastResetDate?: string;
  };
}

const MASK = '****configured****';
const blankConfig = (): AIConfig => ({
  enabled: false,
  primaryProvider: null,
  providers: {},
  features: {}
});

export const AISettings: React.FC = () => {
  const { addNotification } = useAppStore();
  const [providers, setProviders] = useState<ProviderInfo[]>([]);
  const [config, setConfig] = useState<AIConfig>(blankConfig());
  const [keyInputs, setKeyInputs] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, cRes] = await Promise.all([
        apiClient.orgSettings.listAIProviders(),
        apiClient.orgSettings.getAI()
      ]);
      // Array data is not auto-unwrapped by api client; object data is.
      setProviders((pRes as any)?.data || (Array.isArray(pRes) ? pRes : []));
      const cfg = ((cRes as any)?.providers !== undefined ? cRes : (cRes as any)?.data) || blankConfig();
      setConfig({ ...blankConfig(), ...cfg });
      setKeyInputs({});
    } catch (err: any) {
      addNotification?.({ type: 'error', title: 'Load failed', message: err?.response?.data?.error || err.message });
    } finally {
      setLoading(false);
    }
  }, [addNotification]);

  useEffect(() => { load(); }, [load]);

  const updateProviderField = (slug: string, field: keyof ProviderConfig, value: any) => {
    const key = PROVIDER_KEY_BY_SLUG[slug];
    if (!key) return;
    setConfig(prev => ({
      ...prev,
      providers: {
        ...prev.providers,
        [key]: { ...(prev.providers[key] || { enabled: false }), [field]: value }
      }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Build providers payload: include only fields the user changed, plus the
      // raw API key from keyInputs if typed.
      const providersPayload: Record<string, any> = {};
      for (const [slug, key] of Object.entries(PROVIDER_KEY_BY_SLUG)) {
        const cfg = config.providers[key];
        if (!cfg) continue;
        const entry: any = {
          enabled: cfg.enabled,
          model: cfg.model,
          endpoint: cfg.endpoint,
          deploymentName: cfg.deploymentName,
          organization: cfg.organization,
          maxTokens: cfg.maxTokens,
          temperature: cfg.temperature
        };
        const typed = keyInputs[slug];
        if (typed && typed !== MASK) entry.apiKey = typed;
        providersPayload[key] = entry;
      }

      await apiClient.orgSettings.updateAI({
        enabled: config.enabled,
        primaryProvider: config.primaryProvider,
        providers: providersPayload,
        features: config.features
      });
      addNotification?.({ type: 'success', title: 'AI settings saved', message: 'Provider configuration saved.' });
      await load();
    } catch (err: any) {
      addNotification?.({ type: 'error', title: 'Save failed', message: err?.response?.data?.error || err.message });
    } finally {
      setSaving(false);
    }
  };

  const handleValidate = async (slug: string) => {
    const apiKey = keyInputs[slug];
    if (!apiKey) {
      addNotification?.({ type: 'warning', title: 'Type the key first', message: 'Paste an API key to validate format.' });
      return;
    }
    setValidating(slug);
    try {
      const res: any = await apiClient.orgSettings.validateAIKey(slug, apiKey);
      const inner = res?.isValid !== undefined ? res : res?.data;
      if (inner?.isValid) {
        addNotification?.({ type: 'success', title: 'Format OK', message: inner.message || 'Looks valid. Save to test the live connection.' });
      } else {
        addNotification?.({ type: 'error', title: 'Invalid format', message: inner?.error || 'Key format does not match expected pattern.' });
      }
    } catch (err: any) {
      addNotification?.({ type: 'error', title: 'Validation error', message: err?.response?.data?.error || err.message });
    } finally {
      setValidating(null);
    }
  };

  const handleReset = async () => {
    if (!confirm('Reset all AI provider settings? Existing API keys will be deleted.')) return;
    try {
      await apiClient.orgSettings.resetAI();
      addNotification?.({ type: 'success', title: 'Reset', message: 'AI configuration cleared.' });
      await load();
    } catch (err: any) {
      addNotification?.({ type: 'error', title: 'Reset failed', message: err?.response?.data?.error || err.message });
    }
  };

  const usage = config.usage || {};

  if (loading) return <div className="flex justify-center py-12"><LoadingSpinner size="lg" /></div>;

  return (
    <div className="space-y-5">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">AI Providers</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Use your own API key from OpenAI, Anthropic, or another provider. Token costs bill directly to your account, not WorkiDoc's.
          </p>
        </div>

        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={config.enabled}
            onChange={(e) => setConfig(prev => ({ ...prev, enabled: e.target.checked }))}
            className="mt-0.5 w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
          />
          <span>
            <span className="block text-sm font-medium text-gray-900 dark:text-white">Enable AI features for this organization</span>
            <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              When off, classification falls back to keyword matching only — no AI calls are made.
            </span>
          </span>
        </label>

        {config.enabled && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Primary Provider</label>
            <select
              value={config.primaryProvider || ''}
              onChange={(e) => setConfig(prev => ({ ...prev, primaryProvider: e.target.value || null }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="">— select primary —</option>
              {providers.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              All AI features (classification, summarization, response generation) will use this provider's key.
            </p>
          </div>
        )}
      </div>

      {/* Per-provider configuration */}
      {config.enabled && providers.map((p) => {
        const camelKey = PROVIDER_KEY_BY_SLUG[p.id];
        if (!camelKey) return null;
        const provCfg: ProviderConfig = config.providers[camelKey] || { enabled: false };
        const hasSavedKey = !!provCfg.apiKey;
        const isPrimary = config.primaryProvider === p.id;

        return (
          <div
            key={p.id}
            className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm border p-5 space-y-4 ${
              isPrimary ? 'border-blue-300 dark:border-blue-700' : 'border-gray-200 dark:border-gray-700'
            }`}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white">{p.name}</h3>
                  {isPrimary && (
                    <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                      Primary
                    </span>
                  )}
                  {hasSavedKey && (
                    <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">
                      Key saved
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{p.description}</p>
              </div>
              <a
                href={p.setupUrl}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-blue-600 hover:underline whitespace-nowrap"
              >
                Get API key →
              </a>
            </div>

            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={!!provCfg.enabled}
                onChange={(e) => updateProviderField(p.id, 'enabled', e.target.checked)}
                className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Enable {p.name}</span>
            </label>

            {provCfg.enabled && (
              <>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                    API Key {!hasSavedKey && <span className="text-red-500">*</span>}
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="password"
                      autoComplete="new-password"
                      value={keyInputs[p.id] || ''}
                      placeholder={hasSavedKey ? MASK : 'Paste your API key'}
                      onChange={(e) => setKeyInputs(prev => ({ ...prev, [p.id]: e.target.value }))}
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                    />
                    <Button
                      variant="outline"
                      onClick={() => handleValidate(p.id)}
                      disabled={validating === p.id || !keyInputs[p.id]}
                    >
                      {validating === p.id ? <LoadingSpinner size="sm" /> : 'Validate format'}
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Model</label>
                    <select
                      value={provCfg.model || p.defaultModel}
                      onChange={(e) => updateProviderField(p.id, 'model', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                    >
                      {p.models.map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Max tokens</label>
                    <input
                      type="number"
                      min={1}
                      value={provCfg.maxTokens ?? ''}
                      placeholder="e.g. 2000"
                      onChange={(e) => updateProviderField(p.id, 'maxTokens', e.target.value ? parseInt(e.target.value, 10) : undefined)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      Temperature ({(provCfg.temperature ?? 0.3).toFixed(2)})
                    </label>
                    <input
                      type="range"
                      min={0}
                      max={1}
                      step={0.05}
                      value={provCfg.temperature ?? 0.3}
                      onChange={(e) => updateProviderField(p.id, 'temperature', parseFloat(e.target.value))}
                      className="w-full"
                    />
                  </div>
                </div>

                {p.requiresEndpoint && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Endpoint URL</label>
                      <input
                        type="text"
                        value={provCfg.endpoint || ''}
                        placeholder="https://my-resource.openai.azure.com"
                        onChange={(e) => updateProviderField(p.id, 'endpoint', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Deployment name</label>
                      <input
                        type="text"
                        value={provCfg.deploymentName || ''}
                        onChange={(e) => updateProviderField(p.id, 'deploymentName', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                      />
                    </div>
                  </div>
                )}

                {p.id === 'openai' && (
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">OpenAI Organization (optional)</label>
                    <input
                      type="text"
                      value={provCfg.organization || ''}
                      placeholder="org-..."
                      onChange={(e) => updateProviderField(p.id, 'organization', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                    />
                  </div>
                )}
              </>
            )}
          </div>
        );
      })}

      {/* Usage */}
      {config.enabled && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 space-y-3">
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">Usage this month</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <div className="text-gray-500 dark:text-gray-400 text-xs">Tokens</div>
              <div className="font-medium text-gray-900 dark:text-white">{(usage.totalTokensThisMonth || 0).toLocaleString()}</div>
              {usage.monthlyTokenLimit ? (
                <div className="text-xs text-gray-400">of {usage.monthlyTokenLimit.toLocaleString()}</div>
              ) : null}
            </div>
            <div>
              <div className="text-gray-500 dark:text-gray-400 text-xs">Cost (USD)</div>
              <div className="font-medium text-gray-900 dark:text-white">${(usage.totalCostThisMonth || 0).toFixed(4)}</div>
              {usage.monthlyCostLimit ? (
                <div className="text-xs text-gray-400">of ${usage.monthlyCostLimit}</div>
              ) : null}
            </div>
            <div>
              <div className="text-gray-500 dark:text-gray-400 text-xs">Cycle started</div>
              <div className="font-medium text-gray-900 dark:text-white">
                {usage.lastResetDate ? new Date(usage.lastResetDate).toLocaleDateString() : '-'}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-end gap-2">
        <Button variant="outline" onClick={handleReset} disabled={saving}>Reset</Button>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? <><LoadingSpinner size="sm" /> Saving...</> : 'Save AI settings'}
        </Button>
      </div>
    </div>
  );
};
